import React, { useState } from 'react';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { CallbackModal } from '../components/CallbackModal';
import { MapPin, Star, Languages, Calendar, MessageCircle, Phone, CheckCircle2, Plus, Minus, AlertCircle, Clock, Zap } from 'lucide-react';
import { Link } from 'react-router-dom';
const BOOKING_URL = 'https://denticoclinic.patientportal.onl/bookings';
const WHATSAPP_URL = 'https://wa.me/447345197820';
const TELEGRAM_URL = 'https://t.me/dentico_clinic';
function TelegramIcon({
  className
}: {
  className?: string;
}) {
  return <svg viewBox="0 0 24 24" fill="currentColor" className={className} data-id="element-1258">
      <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" data-id="element-1259" />
    </svg>;
}
function AccordionItem({
  question,
  answer,
  isOpen,
  onToggle
}: {
  question: string;
  answer: string;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return <div className="border-b border-gray-100 last:border-b-0" data-id="element-1260">
      <button onClick={onToggle} className="w-full flex items-center justify-between py-4 text-left focus:outline-none group" data-id="element-1261">
        <span className="text-lg font-medium text-gray-900 group-hover:text-teal-600 transition-colors" data-id="element-1262">
          {question}
        </span>
        <span className={`flex-shrink-0 ml-4 w-6 h-6 rounded-full flex items-center justify-center transition-colors ${isOpen ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-400'}`} data-id="element-1263">
          {isOpen ? <Minus className="w-4 h-4" data-id="element-1264" /> : <Plus className="w-4 h-4" data-id="element-1265" />}
        </span>
      </button>
      <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-96 opacity-100 pb-4' : 'max-h-0 opacity-0'}`} data-id="element-1266">
        <p className="text-gray-600 leading-relaxed" data-id="element-1267">{answer}</p>
      </div>
    </div>;
}
export function EmergencyDentistLondonPage() {
  const [isCallbackOpen, setIsCallbackOpen] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);
  const toggleFaq = (index: number) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };
  const faqs = [{
    q: 'Що робити, якщо сильно болить зуб?',
    a: "Якнайшвидше зв'яжіться з нами для невідкладного огляду. Ми визначимо причину болю та запропонуємо лікування. Не грійте хворе місце!"
  }, {
    q: 'Чи приймаєте ви терміново в той же день?',
    a: 'Так, ми завжди намагаємося знайти час для пацієнтів з гострим болем у той же день. Напишіть у WhatsApp/Telegram або зателефонуйте.'
  }, {
    q: 'Скільки коштує екстрений прийом?',
    a: 'Вартість залежить від необхідних процедур та діагностики. Екстрена консультація — £60. Після огляду ми пояснимо варіанти лікування та ціни.'
  }, {
    q: 'Які симптоми вважаються невідкладними?',
    a: 'Сильний зубний біль, набряк обличчя або ясен, гній, підвищена температура, біль при ковтанні, травма зуба (скол, вивих), кровотеча після видалення.'
  }, {
    q: 'Що робити, якщо вибили зуб?',
    a: 'Знайдіть зуб, тримайте його за коронку (не за корінь!), промийте водою, якщо брудний. Спробуйте вставити назад у лунку або помістіть у молоко/слину. Зверніться до лікаря протягом 30-60 хвилин!'
  }, {
    q: 'Чи працюєте ви у вихідні?',
    a: 'Ми працюємо з понеділка по суботу. У невідкладних випадках напишіть нам у WhatsApp, ми спробуємо допомогти або порадити.'
  }, {
    q: 'Чи можна отримати допомогу українською?',
    a: 'Так, у Dentico Clinic можливе спілкування українською мовою. Ви зможете точно описати свої симптоми.'
  }, {
    q: 'Де ви знаходитесь?',
    a: '360–364 City Road, London EC1V 2PY (Angel / Islington), поруч з Angel Station.'
  }];
  // Schemas
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.a
      }
    }))
  };
  const procedureSchema = {
    '@context': 'https://schema.org',
    '@type': 'MedicalProcedure',
    name: 'Emergency Dental Care',
    description: 'Urgent dental treatment for pain relief, trauma, or infection.',
    procedureType: 'http://schema.org/SurgicalProcedure',
    provider: {
      '@type': 'Dentist',
      name: 'Dentico Clinic',
      address: {
        '@type': 'PostalAddress',
        streetAddress: '360-364 City Road',
        addressLocality: 'London',
        postalCode: 'EC1V 2PY',
        addressCountry: 'GB'
      }
    }
  };
  return <Layout data-id="element-1268">
      <CallbackModal isOpen={isCallbackOpen} onClose={() => setIsCallbackOpen(false)} data-id="element-1269" />

      {/* Structured Data */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(faqSchema)
    }} data-id="element-1270" />
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(procedureSchema)
    }} data-id="element-1271" />

      {/* 1. HERO SECTION (Urgent styling) */}
      <section className="relative bg-white overflow-hidden" data-id="element-1272">
        <div className="absolute inset-0 bg-red-50/50 -skew-y-3 origin-top-left transform translate-y-20" data-id="element-1273" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-16 lg:pt-20 lg:pb-24 relative" data-id="element-1274">
          <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-1275">
            <div className="space-y-6" data-id="element-1276">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-100 text-red-800 text-sm font-medium animate-pulse" data-id="element-1277">
                <AlertCircle className="w-4 h-4" data-id="element-1278" />
                Невідкладна допомога
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight tracking-tight" data-id="element-1279">
                Невідкладна стоматологічна допомога у Лондоні –{' '}
                <span className="text-red-600" data-id="element-1280">Dentico Clinic</span>
                <br data-id="element-1281" />
                <span className="text-2xl sm:text-3xl font-medium text-gray-500 mt-2 block" data-id="element-1282">
                  (Angel, Islington)
                </span>
              </h1>

              <p className="text-lg text-gray-600 max-w-lg leading-relaxed" data-id="element-1283">
                Гострий біль? Зламаний зуб? Набряк? Ми приймаємо пацієнтів з
                гострим болем у той же день. Консультація українською мовою.
              </p>

              {/* Trust Badges */}
              <div className="flex flex-wrap gap-3 text-sm font-medium text-gray-700" data-id="element-1284">
                <div className="flex items-center gap-1.5 bg-white px-3 py-1.5 rounded-lg border border-gray-200 shadow-sm" data-id="element-1285">
                  <Clock className="w-4 h-4 text-red-600" data-id="element-1286" />
                  Терміновий прийом
                </div>
                <div className="flex items-center gap-1.5 bg-white px-3 py-1.5 rounded-lg border border-gray-200 shadow-sm" data-id="element-1287">
                  <MapPin className="w-4 h-4 text-teal-600" data-id="element-1288" />
                  Angel, EC1V
                </div>
                <div className="flex items-center gap-1.5 bg-white px-3 py-1.5 rounded-lg border border-gray-200 shadow-sm" data-id="element-1289">
                  <Languages className="w-4 h-4 text-teal-600" data-id="element-1290" />
                  Українська мова
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 pt-4" data-id="element-1291">
                <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-1292">
                  <Button size="lg" className="w-full gap-2 bg-green-600 hover:bg-green-700 text-white shadow-lg shadow-green-600/20" data-id="element-1293">
                    <MessageCircle className="w-5 h-5" data-id="element-1294" />
                    Написати в WhatsApp
                  </Button>
                </a>
                <a href="tel:+447345197820" className="w-full sm:w-auto" data-id="element-1295">
                  <Button variant="outline" size="lg" className="w-full gap-2 border-red-200 text-red-700 hover:bg-red-50" data-id="element-1296">
                    <Phone className="w-5 h-5" data-id="element-1297" />
                    +44 7345 197820
                  </Button>
                </a>
              </div>
            </div>

            <div className="relative" data-id="element-1298">
              <div className="absolute inset-0 bg-red-100 rounded-3xl transform rotate-3 blur-sm opacity-30" data-id="element-1299"></div>
              <img src="https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?auto=format&fit=crop&w=1200&q=80" alt="Невідкладна стоматологія Лондон" className="relative rounded-3xl shadow-2xl object-cover h-[300px] sm:h-[400px] lg:h-[500px] w-full grayscale-[20%]" data-id="element-1300" />
            </div>
          </div>
        </div>
      </section>

      {/* 2. WHEN TO SEEK HELP */}
      <section className="py-16 bg-white" data-id="element-1301">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1302">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center" data-id="element-1303">
            Коли потрібна невідкладна допомога?
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1304">
            {[{
            title: 'Сильний зубний біль',
            desc: 'Біль, який не проходить від знеболюючих, пульсуючий або нічний біль.'
          }, {
            title: 'Набряк обличчя',
            desc: 'Припухлість щоки або ясен може свідчити про серйозну інфекцію.'
          }, {
            title: 'Травма зуба',
            desc: 'Зламаний, сколотий або вибитий зуб внаслідок удару чи падіння.'
          }, {
            title: 'Втрата пломби/коронки',
            desc: 'Якщо випала пломба або коронка, і зуб став чутливим або гострим.'
          }, {
            title: 'Кровотеча',
            desc: 'Кровотеча з ясен після видалення зуба, яка не зупиняється.'
          }, {
            title: 'Біль при ковтанні',
            desc: 'Ускладнене ковтання або дихання — це ознака небезпечного поширення інфекції.'
          }].map((item, idx) => <Card key={idx} className="p-6 border-l-4 border-l-red-500" data-id="element-1305">
                <h3 className="text-lg font-bold text-gray-900 mb-2 flex items-center gap-2" data-id="element-1306">
                  <AlertCircle className="w-5 h-5 text-red-500" data-id="element-1307" />
                  {item.title}
                </h3>
                <p className="text-gray-600" data-id="element-1308">{item.desc}</p>
              </Card>)}
          </div>
        </div>
      </section>

      {/* 3. FIRST AID */}
      <section className="py-16 bg-red-50" data-id="element-1309">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1310">
          <div className="text-center mb-10" data-id="element-1311">
            <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-1312">
              Що робити до візиту до лікаря?
            </h2>
            <p className="text-lg text-gray-600" data-id="element-1313">
              Поради першої допомоги, поки ви чекаєте на прийом.
            </p>
          </div>
          <div className="space-y-4" data-id="element-1314">
            <div className="bg-white p-6 rounded-xl shadow-sm" data-id="element-1315">
              <h3 className="font-bold text-lg mb-2 text-gray-900" data-id="element-1316">
                При зубному болю
              </h3>
              <p className="text-gray-600" data-id="element-1317">
                Прийміть знеболююче (ібупрофен або парацетамол), прополощіть рот
                теплою водою. Не прикладайте аспірин до ясен і не грійте хворе
                місце!
              </p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-sm" data-id="element-1318">
              <h3 className="font-bold text-lg mb-2 text-gray-900" data-id="element-1319">
                Якщо вибили зуб
              </h3>
              <p className="text-gray-600" data-id="element-1320">
                Тримайте зуб тільки за коронку (білу частину). Якщо брудний —
                промийте молоком або водою. Спробуйте вставити назад у лунку або
                покладіть у молоко/слину. Час критичний — у вас є 30-60 хвилин!
              </p>
            </div>
            <div className="bg-white p-6 rounded-xl shadow-sm" data-id="element-1321">
              <h3 className="font-bold text-lg mb-2 text-gray-900" data-id="element-1322">
                При набряку
              </h3>
              <p className="text-gray-600" data-id="element-1323">
                Прикладіть холодний компрес до щоки ззовні. Це зменшить набряк і
                біль. Не використовуйте тепло!
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 4. OUR PROCESS */}
      <section className="py-16 bg-white" data-id="element-1324">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1325">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center" data-id="element-1326">
            Як ми працюємо в екстрених випадках
          </h2>
          <div className="grid md:grid-cols-4 gap-8" data-id="element-1327">
            {[{
            step: '01',
            title: "Зв'язок",
            desc: 'Напишіть нам у WhatsApp або зателефонуйте. Опишіть симптоми.'
          }, {
            step: '02',
            title: 'Оцінка',
            desc: 'Ми оцінимо терміновість і знайдемо найближчий час для візиту.'
          }, {
            step: '03',
            title: 'Візит',
            desc: 'Огляд, рентген-діагностика та зняття гострого болю.'
          }, {
            step: '04',
            title: 'План',
            desc: 'Якщо потрібне подальше лікування, ми складемо план дій.'
          }].map((item, idx) => <Card key={idx} className="p-6 relative overflow-hidden" data-id="element-1328">
                <div className="text-6xl font-bold text-red-50 absolute -top-4 -right-4 select-none" data-id="element-1329">
                  {item.step}
                </div>
                <div className="relative z-10" data-id="element-1330">
                  <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-1331">
                    {item.title}
                  </h3>
                  <p className="text-gray-600" data-id="element-1332">{item.desc}</p>
                </div>
              </Card>)}
          </div>
        </div>
      </section>

      {/* 5. COMMON TREATMENTS */}
      <section className="py-16 bg-gray-50" data-id="element-1333">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1334">
          <div className="text-center mb-12" data-id="element-1335">
            <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-1336">
              Види невідкладної допомоги
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1337">
            {[{
            title: 'Зняття гострого болю',
            desc: 'Медикаментозна обробка та тимчасова пломба.'
          }, {
            title: 'Видалення зуба',
            desc: 'Якщо зуб неможливо врятувати або він є джерелом інфекції.'
          }, {
            title: 'Лікування каналів',
            desc: 'Видалення запаленого нерва (пульпектомія) для порятунку зуба.'
          }, {
            title: 'Фіксація коронки',
            desc: 'Цементування коронки або моста, що випали.'
          }, {
            title: 'Розкриття абсцесу',
            desc: 'Дренаж гною для зняття набряку та болю.'
          }, {
            title: 'Шинування зубів',
            desc: 'Фіксація рухомих зубів після травми.'
          }].map((item, idx) => <Card key={idx} className="p-6 bg-white" data-id="element-1338">
                <Zap className="w-8 h-8 text-red-500 mb-4" data-id="element-1339" />
                <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-1340">
                  {item.title}
                </h3>
                <p className="text-gray-600" data-id="element-1341">{item.desc}</p>
              </Card>)}
          </div>
        </div>
      </section>

      {/* MID-PAGE CTA (Urgent) */}
      <section className="py-12 bg-red-600" data-id="element-1342">
        <div className="max-w-4xl mx-auto px-4 text-center" data-id="element-1343">
          <h2 className="text-2xl lg:text-3xl font-bold text-white mb-4" data-id="element-1344">
            Болить зуб прямо зараз?
          </h2>
          <p className="text-red-100 mb-6" data-id="element-1345">
            Не чекайте — напишіть нам або зателефонуйте для термінового прийому.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-3" data-id="element-1346">
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" data-id="element-1347">
              <Button size="lg" className="gap-2 bg-green-600 hover:bg-green-700 text-white border-none" data-id="element-1348">
                <MessageCircle className="w-5 h-5" data-id="element-1349" />
                WhatsApp
              </Button>
            </a>
            <a href="tel:+447345197820" data-id="element-1350">
              <Button variant="outline" size="lg" className="gap-2 border-white text-white hover:bg-white/10" data-id="element-1351">
                <Phone className="w-5 h-5" data-id="element-1352" />
                +44 7345 197820
              </Button>
            </a>
            <Button variant="outline" size="lg" className="gap-2 border-white text-white hover:bg-white/10" onClick={() => setIsCallbackOpen(true)} data-id="element-1353">
              <Phone className="w-5 h-5" data-id="element-1354" />
              Замовити дзвінок
            </Button>
          </div>
        </div>
      </section>

      {/* 6. FAQ */}
      <section className="py-16 bg-white" data-id="element-1355">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1356">
          <div className="text-center mb-10" data-id="element-1357">
            <h2 className="text-3xl font-bold text-gray-900 mb-4" data-id="element-1358">
              Часті запитання
            </h2>
          </div>
          <div className="space-y-2" data-id="element-1359">
            {faqs.map((faq, idx) => <AccordionItem key={idx} question={faq.q} answer={faq.a} isOpen={openFaqIndex === idx} onToggle={() => toggleFaq(idx)} data-id="element-1360" />)}
          </div>
        </div>
      </section>

      {/* 7. INTERNAL LINKS */}
      <section className="py-12 bg-gray-50 border-t border-gray-100" data-id="element-1361">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1362">
          <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-1363">
            Дивіться також
          </h2>
          <div className="flex flex-wrap gap-4" data-id="element-1364">
            <Link to="/ua/ukrainskyi-stomatolog-london" data-id="element-1365">
              <Button variant="outline" size="sm" data-id="element-1366">
                Український стоматолог
              </Button>
            </Link>
            <Link to="/ua/root-canal-london" data-id="element-1367">
              <Button variant="outline" size="sm" data-id="element-1368">
                Лікування каналів
              </Button>
            </Link>
            <Link to="/ua/implanty-london" data-id="element-1369">
              <Button variant="outline" size="sm" data-id="element-1370">
                Імплантація
              </Button>
            </Link>
            <Link to="/ua/veneers-london" data-id="element-1371">
              <Button variant="outline" size="sm" data-id="element-1372">
                Вініри
              </Button>
            </Link>
            <Link to="/ua/services/extraction" data-id="element-1373">
              <Button variant="outline" size="sm" data-id="element-1374">
                Видалення зубів
              </Button>
            </Link>
            <Link to="/ua/prices" data-id="element-1375">
              <Button variant="outline" size="sm" data-id="element-1376">
                Ціни
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* 8. CTA (Urgent) */}
      <section className="py-16 bg-red-900" data-id="element-1377">
        <div className="max-w-4xl mx-auto px-4 text-center" data-id="element-1378">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4" data-id="element-1379">
            Потрібна термінова допомога?
          </h2>
          <p className="text-xl text-red-100 mb-8 max-w-2xl mx-auto" data-id="element-1380">
            Не чекайте, поки біль посилиться. Зв'яжіться з нами зараз.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-1381">
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-1382">
              <Button size="lg" className="w-full gap-2 bg-green-600 hover:bg-green-700 text-white border-none" data-id="element-1383">
                <MessageCircle className="w-5 h-5" data-id="element-1384" />
                WhatsApp
              </Button>
            </a>
            <a href="tel:+447345197820" className="w-full sm:w-auto" data-id="element-1385">
              <Button variant="outline" size="lg" className="w-full gap-2 border-white text-white hover:bg-white/10" data-id="element-1386">
                <Phone className="w-5 h-5" data-id="element-1387" />
                +44 7345 197820
              </Button>
            </a>
            <div className="w-full sm:w-auto" data-id="element-1388">
              <Button variant="outline" size="lg" className="w-full gap-2 border-white text-white hover:bg-white/10" onClick={() => setIsCallbackOpen(true)} data-id="element-1389">
                <Phone className="w-5 h-5" data-id="element-1390" />
                Замовити дзвінок
              </Button>
            </div>
          </div>
        </div>
      </section>
    </Layout>;
}